import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Zap, Star, CheckCircle2, CalendarX } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { usePick } from "@/hooks/usePick";
import { fmtTime, keyLabel, todayKey } from "@/lib/format";
import { EmptyState, ErrorBox, PageHeader, Spinner, TeamMark } from "@/components/Bits";
import CopyPicksButton from "@/components/CopyPicksButton";

function Row({ g, tz, pick, saving }) {
  return (
    <li className={`mpl-card mpl-rise p-3 ${g.featured ? "border-[#B7F52A]/40" : ""}`} data-testid={`quick-${g.id}`}>
      <p className="mb-2 flex items-center justify-between text-xs text-slate-400">
        <span className="font-mono-num">{fmtTime(g.start_time, tz)}</span>
        {g.featured && <span className="flex items-center gap-1 font-bold text-[#B7F52A]" data-testid={`quick-featured-${g.id}`}><Star size={12} fill="currentColor" /> Match vedette{g.featured_points ? ` · +${g.featured_points} pts` : ""}</span>}
      </p>
      <div className="grid grid-cols-2 gap-2">{["away", "home"].map((side) => (
        <button key={side} onClick={() => pick(g, side)} disabled={saving === g.id} data-testid={`quick-pick-${g.id}-${side}`} aria-pressed={g.my_pick === side}
          className={`flex items-center gap-2 rounded-xl border p-3 text-left transition-[background-color,border-color,transform] active:scale-[0.97] ${g.my_pick === side ? "border-[#B7F52A] bg-[#B7F52A] text-[#101310]" : "border-white/10 hover:border-white/30"}`}>
          <TeamMark team={g[side]} size={30} />
          <span className="min-w-0"><span className="block font-display text-lg font-extrabold uppercase leading-none">{g[side].abbr}</span>
            <span className={`block truncate text-[11px] ${g.my_pick === side ? "text-[#101310]/70" : "text-slate-400"}`}>{side === "home" ? "à domicile" : "à l'extérieur"}</span></span>
        </button>))}</div>
    </li>
  );
}

export default function QuickPicks() {
  const { leagueId, tz } = useAuth();
  const [day, setDay] = useState(todayKey());
  const [games, setGames] = useState(null);
  const [error, setError] = useState("");
  const { pick, saving } = usePick(setGames);
  useEffect(() => {
    if (!leagueId) { return; }
    api.get("/games", { params: { league_id: leagueId, date: day } }).then(({ data }) => {
      const open = data.games.filter((g) => !g.excluded && !g.locked);
      if (!open.length && data.next_date && day === todayKey()) setDay(data.next_date);
      else setGames(open);
    }).catch((e) => setError(errMsg(e)));
  }, [leagueId, day]);
  if (error) return <ErrorBox message={error} />;
  if (!games) return <Spinner />;
  const done = games.filter((g) => g.my_pick).length;
  return (
    <div className="mx-auto max-w-xl" data-testid="quick-picks">
      <PageHeader eyebrow={keyLabel(day)} title="Pronos rapides" />
      <p className="-mt-3 mb-4 text-xs text-slate-400" data-testid="quick-all-leagues">Tes pronos sont propres à cette ligue : tu peux faire des choix différents dans chaque ligue, ou les copier dans tes autres ligues.</p>
      <CopyPicksButton leagueId={leagueId} gameIds={(games || []).filter((g) => g.my_pick && !g.locked).map((g) => g.id)} label="Copier tous mes pronos du jour dans mes autres ligues" testId="quick-copy-all" className="mb-4 w-full justify-center" />
      {games.length ? (<>
        <div className="mb-4 flex items-center gap-3" data-testid="quick-progress">
          <div className="h-2 flex-1 overflow-hidden rounded-full bg-white/10"><div className="h-full rounded-full bg-[#B7F52A] transition-[width] duration-500" style={{ width: `${(done / games.length) * 100}%` }} /></div>
          <span className="font-mono-num text-sm font-bold">{done}/{games.length}</span>
        </div>
        <ul className="space-y-2">{games.map((g) => <Row key={g.id} g={g} tz={tz} pick={pick} saving={saving} />)}</ul>
        {done === games.length && <p className="mt-4 flex items-center gap-2 rounded-xl bg-[#B7F52A]/10 p-3 text-sm font-semibold text-[#B7F52A]" data-testid="quick-all-done"><CheckCircle2 size={16} /> Tout est fait ! Tu peux modifier tes choix jusqu'au début de chaque match.</p>}
      </>) : <EmptyState icon={CalendarX} title="Aucun match à pronostiquer" text="Les prochains matchs apparaîtront ici dès leur publication." testId="quick-empty" />}
      <p className="mt-5 flex items-start gap-2 text-xs text-slate-400" data-testid="quick-no-penalty"><Zap size={13} className="mt-0.5 shrink-0 text-[#B7F52A]" /> Un soir raté ? Aucun point perdu et ta série n'est pas cassée : seuls les matchs pronostiqués comptent.</p>
      <Link to="/matchs" className="mt-3 inline-block text-xs font-semibold text-[#B7F52A]" data-testid="quick-full-link">Voir les matchs en détail (questions bonus) →</Link>
    </div>
  );
}
